import React from 'react'
import { Navigate } from 'react-router-dom'

import ProtectedRoute from '@/components/router/protected-route'
import { useAppDispatch, useAppSelector } from '@/hooks/reduxHooks'
import SeparateMapLayout from '@/layouts/separateMap/layout'
import { readAuthFlag, readUser } from '@/store/services/local-storage.service'
import { doLogout } from '@/store/slices/auth-slice'
import { getProfileData } from '@/store/slices/profile-slice'

import { DetachedMapLoading } from './DetachedMapLoading'

/** Bramka trasy /separated-map. Trasa żyje POZA MainLayoutem, więc nikt inny nie
 *  pobiera tu profilu, a `ProtectedRoute` czekałby na niego loaderem w nieskończoność —
 *  okno odczepione (`window.open`) startuje z zimnym store'em. Bootstrap używa tego
 *  samego thunka i warunku co powłoka; przy błędzie `ProtectedRoute` pokazuje swój
 *  ekran ponowienia.
 *
 *  Oba stany ładowania renderują się zanim trasa potomna zamontuje `SeparateMapLayout`,
 *  więc bramka wnosi go sama — inaczej ekran nie ma klasy motywu i przy jasnym motywie
 *  mignąłby ciemny. */
export const SeparateMapGate = () => {
  const dispatch = useAppDispatch()
  const profile = useAppSelector((state) => state.profile?.data)
  const status = useAppSelector((state) => state.profile.status)

  const brokenSession = readAuthFlag() && !readUser()

  React.useEffect(() => {
    if (brokenSession) {
      dispatch(doLogout())
      return
    }

    const userId = readUser()
    if (readAuthFlag() && userId && !profile && status === 'idle') dispatch(getProfileData(userId))
    // Tylko zimny start okna — nawigacja wewnątrz aplikacji ma już profil w store.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadingScreen = (
    <SeparateMapLayout>
      <DetachedMapLoading />
    </SeparateMapLayout>
  )

  if (brokenSession) {
    return <Navigate to='/auth/login' replace />
  }

  // Warunek powtarza strażnik `ProtectedRoute`, ale dopiero po sprawdzeniu flagi sesji:
  // bez zalogowania profil nigdy nie przyjdzie i ekran ładowania zostałby na zawsze
  // zamiast przekierowania na /auth/login.
  if (readAuthFlag() && !profile && (status === 'loading' || status === 'idle')) {
    return loadingScreen
  }

  return (
    <React.Suspense fallback={loadingScreen}>
      <ProtectedRoute requiredRoles={['ADMIN', 'USER']} />
    </React.Suspense>
  )
}
